import Link from 'next/link';
import React, { useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import ArrowDownThinIcon from '../../assets/ArrowDownThinIcon';
import ChevronRight from '../../assets/ChevronRight';
import SearchIcon from '../../assets/SearchIcon';
import XIcon from '../../assets/XIcon';
import type { DataWorldChartTypes } from '../../types/types';
import { formatNumber } from '../../utils/format';
import { getPercentageString } from '../../utils/string';
import RenderLayout, { Platform } from '../core/render-layout';

interface ProgressBarProps {
  progress: number;
  totalBars?: number;
  activeColor?: string;
  inactiveColor?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  totalBars = 28,
  activeColor = 'bg-[#34a28a]',
  inactiveColor = 'bg-zinc-300',
}) => {
  const activeBars = Math.round((progress / 100) * totalBars);

  return (
    <div className="inline-flex h-2.5 items-center justify-start gap-0.5 rounded">
      {[...Array(totalBars)].map((_, index) => (
        <div
          key={index}
          className={`h-2.5 w-0.5 rounded ${
            index < activeBars ? activeColor : inactiveColor
          }`}
        />
      ))}
    </div>
  );
};

type RecordedCountriesTableProps = {
  title?: string;
  countries: DataWorldChartTypes[];
};

export default function RecordedCountriesTable({
  title,
  countries,
}: Readonly<RecordedCountriesTableProps>) {
  const [searchString, setSearchString] = React.useState('');
  const [sortLabel, setSortLabel] = React.useState<string>('living');
  const [sortKey, setSortKey] = React.useState<
    keyof DataWorldChartTypes | ((item: DataWorldChartTypes) => number)
  >('living');
  const [sortOrder, setSortOrder] = React.useState('desc');

  const sortHandler =
    (
      label: string,
      keyOrFunction:
        | keyof DataWorldChartTypes
        | ((item: DataWorldChartTypes) => number)
    ) =>
    () => {
      setSortLabel(() => label);
      setSortKey(() => keyOrFunction); // Store the key or function
      setSortOrder((prevSortOrder) =>
        sortLabel === label
          ? prevSortOrder === 'asc'
            ? 'desc'
            : 'asc'
          : 'desc'
      );
    };

  const arrowedText = (label: string, className: string) => {
    if (sortLabel === label) {
      return (
        <div className={`flex w-full gap-0.5 ${className}`}>
          <ArrowDownThinIcon
            height={16}
            width={16}
            className={sortOrder === 'desc' ? 'rotate-0' : 'rotate-180'}
          />
          <p>{label}</p>
        </div>
      );
    }

    return <p>{label}</p>;
  };

  const sortedCountries = useMemo(() => {
    if (!countries || !countries?.length || !Array.isArray(countries)) {
      return [];
    }

    const countriesCopy = [...countries].filter(
      (country: DataWorldChartTypes) =>
        country?.name?.toLowerCase() !== 'world' &&
        country?.name?.toLowerCase().includes(searchString.toLowerCase())
    );

    return countriesCopy?.sort((a, b) => {
      const valueA =
        typeof sortKey === 'function'
          ? sortKey(a)
          : a[sortKey as keyof DataWorldChartTypes];
      const valueB =
        typeof sortKey === 'function'
          ? sortKey(b)
          : b[sortKey as keyof DataWorldChartTypes];

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortOrder === 'desc' ? valueB - valueA : valueA - valueB;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortOrder === 'desc'
          ? valueB.localeCompare(valueA)
          : valueA.localeCompare(valueB);
      }

      return 0;
    });
  }, [countries, searchString, sortKey, sortOrder]);

  const _renderMobileLayout = () => {
    if (!countries?.length) {
      return <></>;
    }

    return (
      <div
        className="m-[20px] flex flex-col gap-4 rounded-lg border-DEFAULT border-solid border-[#E7E8E9]
           bg-white p-6 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)] md:mx-[45px]"
      >
        <p className="text-base font-semibold leading-normal text-zinc-950">
          All countries{' '}
          <span className="text-base font-medium leading-normal text-zinc-500">
            ({sortedCountries.length})
          </span>
        </p>
        <div className="inline-flex h-10 w-full items-center justify-between overflow-hidden rounded-md border border-zinc-300 bg-white px-3.5">
          <input
            placeholder="Search"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            className="shrink grow basis-0 text-sm font-normal leading-tight text-zinc-400 focus:outline-none"
          ></input>
          {!searchString ? (
            <SearchIcon width={20} height={20} fill="#a1a1aa" />
          ) : (
            <button
              className="cursor-pointer border-0 bg-transparent outline-0"
              onClick={(e) => {
                e.preventDefault();
                setSearchString('');
              }}
            >
              <XIcon width={20} height={20} fill="#a1a1aa" />
            </button>
          )}
        </div>
        <div className="max-h-[600px] overflow-scroll">
          {sortedCountries?.map((country) => (
            <Link
              key={`mobile-${country.ID}`}
              href={`/country/${country.name
                ?.toLowerCase()
                ?.split(' ')
                ?.join('-')}`}
              className="flex flex-col gap-4 rounded-lg p-3 hover:bg-gray-100"
            >
              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  <ReactCountryFlag
                    countryCode={country?.iso_2}
                    svg
                    alt={`${country?.name} flag`}
                    cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                    cdnSuffix="svg"
                    style={{
                      borderRadius: '9999px',
                      width: '20px',
                      height: '20px',
                    }}
                  />
                </div>
                <span className="text-sm font-semibold leading-tight text-zinc-950">
                  {country.name}
                </span>
                <div className="ml-auto">
                  <ChevronRight />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-start">
                    <span className="text-sm font-normal leading-tight text-zinc-500">
                      Progress
                    </span>
                  </div>
                  <div className="flex items-center justify-end">
                    <ProgressBar
                      progress={
                        country.population !== 0
                          ? (country.living / country.population) * 100
                          : 0
                      }
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-start">
                    <span className="text-sm font-normal leading-tight text-zinc-500">
                      Records
                    </span>
                  </div>
                  <div className="flex items-center justify-end">
                    <span className="justify-center text-right text-sm font-medium leading-tight text-zinc-950">
                      {formatNumber(country.living)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center justify-start">
                    <span className="text-sm font-normal leading-tight text-zinc-500">
                      Population
                    </span>
                  </div>
                  <div className="flex items-center justify-end">
                    <span className="justify-center text-right text-sm font-medium leading-tight text-zinc-950">
                      {formatNumber(country.population)}
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  };

  const _renderWebLayout = () => {
    if (!countries?.length) {
      return <></>;
    }

    return (
      <div className="mx-[80px] mb-[60px] mt-[24px] flex flex-col gap-6 rounded-lg border-DEFAULT border-solid border-[#E7E8E9] bg-white p-8 shadow-[0px_2px_16px_-4px_rgba(9,9,11,0.08)]">
        <p className="text-lg font-semibold leading-7 text-zinc-950">
          {title || 'All countries'}
        </p>
        <div className="inline-flex h-10 w-[260px] items-center justify-between overflow-hidden rounded-md border border-zinc-300 bg-white px-3.5">
          <input
            placeholder="Search"
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            className="shrink grow basis-0 text-sm font-normal leading-tight text-zinc-400 focus:outline-none"
          ></input>
          {!searchString ? (
            <SearchIcon width={20} height={20} fill="#a1a1aa" />
          ) : (
            <button
              className="cursor-pointer border-0 bg-transparent outline-0"
              onClick={(e) => {
                e.preventDefault();
                setSearchString('');
              }}
            >
              <XIcon width={20} height={20} fill="#a1a1aa" />
            </button>
          )}
        </div>
        <div className="max-h-[600px] overflow-auto [&::-webkit-scrollbar]:hidden">
          <table className="w-full">
            <thead className="sticky top-0 z-10 border-b border-b-zinc-200 bg-white">
              <tr className="px-4">
                <th
                  onClick={sortHandler('Country', 'ID')}
                  className="w-[30%] px-[32px] py-[16px] text-left text-sm font-normal leading-tight text-zinc-500"
                >
                  {arrowedText('Country', 'justify-start')}
                </th>
                <th
                  onClick={sortHandler('Progress', (country) =>
                    country.population !== 0
                      ? (country.living / country.population) * 100
                      : 0
                  )}
                  className="w-[17.5%] px-[32px] py-[16px] text-right text-sm font-normal leading-tight text-zinc-500"
                >
                  {arrowedText('Progress', 'justify-end')}
                </th>
                <th
                  onClick={sortHandler('Population', 'population')}
                  className="w-[17.5%] px-[32px] py-[16px] text-right text-sm font-normal leading-tight text-zinc-500"
                >
                  {arrowedText('Population', 'justify-end')}
                </th>
                <th
                  onClick={sortHandler('Records', 'record_count')}
                  className="w-[17.5%] px-[32px] py-[16px] text-right text-sm font-normal leading-tight text-zinc-500"
                >
                  {arrowedText('Records', 'justify-end')}
                </th>
                <th
                  onClick={sortHandler('Completion Index', 'completion_index')}
                  className="w-[17.5%] px-[32px] py-[16px] text-right text-sm font-normal leading-tight text-zinc-500"
                >
                  {arrowedText('Completion Index', 'justify-end')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCountries?.map((country) => (
                <tr
                  key={country.ID}
                  className="border-t-solid border-t-DEFAULT border-y-[#E7E8E9] hover:bg-[#F7F7F8]"
                >
                  <td className="w-[30%]">
                    <Link
                      className="flex size-full items-center justify-start gap-2 px-[32px] py-[14px] text-base font-medium leading-normal text-zinc-950"
                      href={`/country/${country.name
                        ?.toLowerCase()
                        ?.split(' ')
                        ?.join('-')}`}
                    >
                      <ReactCountryFlag
                        countryCode={country?.iso_2}
                        svg
                        alt={`${country?.name} flag`}
                        cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                        cdnSuffix="svg"
                        style={{
                          borderRadius: '9999px',
                          width: '20px',
                          height: '20px',
                        }}
                      />
                      {country.name}
                    </Link>
                  </td>
                  <td className="w-[17.5%] text-right">
                    <Link
                      className="flex size-full items-center justify-end gap-3 px-[32px] py-[14px] text-base font-normal leading-normal text-zinc-950"
                      href={`/country/${country.name
                        ?.toLowerCase()
                        ?.split(' ')
                        ?.join('-')}`}
                    >
                      <ProgressBar
                        progress={
                          country.population !== 0
                            ? (country.living / country.population) * 100
                            : 0
                        }
                      />
                      <p className="min-w-10">
                        {getPercentageString(
                          Math.min(
                            country.population !== 0
                              ? (country.living / country.population) * 100
                              : 0,
                            99
                          ),
                          {
                            fixedDecimals: 0,
                            lessThanOneSpecialCase: true,
                          }
                        )}
                      </p>
                    </Link>
                  </td>
                  <td className="w-[17.5%]">
                    <Link
                      className="flex size-full items-center justify-end gap-2 px-[32px] py-[14px] text-base font-normal leading-normal text-zinc-950"
                      href={`/country/${country.name
                        ?.toLowerCase()
                        ?.split(' ')
                        ?.join('-')}`}
                    >
                      {formatNumber(country.population)}
                    </Link>
                  </td>
                  <td className="w-[17.5%]">
                    <Link
                      className="flex size-full items-center justify-end gap-2 px-[32px] py-[14px] text-base font-normal leading-normal text-zinc-950"
                      href={`/country/${country.name
                        ?.toLowerCase()
                        ?.split(' ')
                        ?.join('-')}`}
                    >
                      {formatNumber(country.record_count)}
                    </Link>
                  </td>
                  <td className="relative w-[17.5%]">
                    <Link
                      className="flex size-full items-center justify-end px-[32px] py-[12px] font-normal"
                      href={`/country/${country.name
                        ?.toLowerCase()
                        ?.split(' ')
                        ?.join('-')}`}
                    >
                      {formatNumber(country.completion_index)}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <RenderLayout platforms={[Platform.DESKTOP]}>
        {_renderWebLayout()}
      </RenderLayout>
      <RenderLayout platforms={[Platform.MOBILE, Platform.TABLET]}>
        {_renderMobileLayout()}
      </RenderLayout>
    </div>
  );
}
